import { OrthographicView, OrthographicViewState } from "@deck.gl/core";
import { ScatterplotLayer } from "@deck.gl/layers";
import { DeckGL } from "@deck.gl/react";
import { useState } from "react";
import { ColorMap, HoverInfo } from "../types";
import UmapTooltip from "./umapTooltip";

interface UmapDataPoint {
  umap1: number;
  umap2: number;
  [key: string]: any;
}

interface UmapDeckLayerProps<T extends UmapDataPoint> {
  data: T[];
  colorMap: ColorMap;
  colorBy: string;
}

const UmapDeckLayer = <T extends UmapDataPoint>({
  data,
  colorMap,
  colorBy,
}: UmapDeckLayerProps<T>) => {
  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);

  const initialViewState: OrthographicViewState = {
    target: [0, 0, 0] as [number, number, number],
    zoom: 3.5,
    minZoom: 3,
    maxZoom: 10,
  };

  const getPointColor = (d: T): [number, number, number, number] => {
    const value = d[colorBy];
    const rgbColor = colorMap[value] || [100, 100, 100];
    return [...rgbColor, 70] as [number, number, number, number];
  };

  const layers = [
    new ScatterplotLayer({
      id: "umap-points",
      data,
      pickable: true,
      opacity: 0.9,
      stroked: false,
      filled: true,
      radiusScale: 4,
      radiusMinPixels: 2,
      radiusMaxPixels: 6,
      lineWidthMinPixels: 1,
      getPosition: (d: T) => [d.umap1, d.umap2, 0] as [number, number, number],
      getRadius: () => 3,
      getFillColor: getPointColor,
      getLineColor: [0, 0, 0],
      onHover: (info: any) => setHoverInfo(info),
    }),
  ];

  return (
    <div className="relative w-full h-[600px]">
      <DeckGL
        views={new OrthographicView({ flipY: true })}
        layers={layers}
        initialViewState={initialViewState}
        controller={true}
      />

      {hoverInfo && hoverInfo.object && (
        <UmapTooltip info={hoverInfo} hoveredObject={hoverInfo.object} />
      )}
    </div>
  );
};

export default UmapDeckLayer;
